/**
 * `/settings/connections/:provider/callback` — where Zitadel redirects the
 * browser back after "Koble til <provider>" completes at the external IdP
 * (both the success and failure case, since IdpLinkControl passes THIS SAME
 * route as both `successUrl` and `failureUrl` — Zitadel distinguishes the two
 * via query params, not via which URL it lands on; see
 * infra/zitadel/CONNECT-GOOGLE-SPEC.md §1/§3). One route serves every
 * provider: `:provider` selects which configured provider (idpId + display
 * copy) to use via identity/providers.ts — it is never used to pick the
 * AddIDPLink target account, only which external IdP's intent is being
 * completed.
 *
 * Token discipline: Zitadel's short-lived idp-intent `id`/`token` (NOT our
 * OIDC access token) arrive as query params. They are read ONCE on mount and
 * the URL is immediately scrubbed via `history.replaceState` — mirroring
 * oidc.ts's `onSigninCallback` — so the intent token never lingers in
 * browser history. The OIDC access token itself is a prop, never read from
 * the URL, and is placed only in the Authorization header by idpLink.ts.
 */

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  type IdpLinkOutcome,
  completeIdpLinkCallback,
  parseIdpLinkCallbackParams,
} from "../identity/idpLink";
import { getConfiguredLinkProvider } from "../identity/providers";
import { IDP_LINK_RETURN_TO_KEY } from "../components/IdpLinkControl";

type CallbackState = { status: "loading" } | IdpLinkOutcome;

export function IdpLinkCallbackPage({
  subject,
  accessToken,
  onSessionExpired,
}: {
  subject: string;
  accessToken: string;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider: providerKey } = useParams<{ provider: string }>();
  const provider = getConfiguredLinkProvider(providerKey ?? "");
  const [outcome, setOutcome] = useState<CallbackState>({ status: "loading" });
  // React 18 StrictMode double-invokes effects in dev; the intent token is
  // single-use, so this guard ensures we only ever call the Zitadel API once.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const search = location.search;
    // Scrub id/token/user (or error/error_description) from the address bar
    // immediately, before doing anything async with them — a REPLACE
    // navigation (not push), so the intent token never lingers in history.
    // Uses react-router's navigate (not raw history.replaceState) so this
    // works identically under BrowserRouter (prod) and MemoryRouter (tests).
    navigate(location.pathname, { replace: true });

    if (!provider) {
      // An unknown/unconfigured :provider — never call the Zitadel API with
      // a bogus idpId. Surfaces as a plain error, not a crash.
      setOutcome({ status: "error", message: "Ukjent eller ikke konfigurert tilbyder." });
      return;
    }

    const params = parseIdpLinkCallbackParams(search);
    completeIdpLinkCallback(params, subject, provider.idpId, accessToken)
      .then(setOutcome)
      .catch((err: unknown) => {
        setOutcome({
          status: "error",
          message: err instanceof Error ? err.message : "Ukjent feil.",
        });
      });
    // Intentionally run once on mount, using the location/provider captured
    // at first render — the effect must not re-run on the scrub navigation
    // above.
  }, []);

  function returnToOrigin(): void {
    const returnTo = window.sessionStorage.getItem(IDP_LINK_RETURN_TO_KEY) ?? "/";
    window.sessionStorage.removeItem(IDP_LINK_RETURN_TO_KEY);
    navigate(returnTo, { replace: true });
  }

  const displayName = provider?.displayName ?? "tilbyderen";

  return (
    <div className="center-screen">
      <section className="auth-card" data-testid="idp-link-callback">
        <h1>
          deSjeff
          <span className="dot" />
        </h1>

        {outcome.status === "loading" && (
          <p data-testid="idp-link-callback-loading">Fullfører {displayName}-kobling …</p>
        )}

        {outcome.status === "success" && (
          <>
            <p data-testid="idp-link-callback-success">{displayName} er koblet til kontoen din.</p>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="idp-link-callback-continue"
              onClick={returnToOrigin}
            >
              Fortsett
            </button>
          </>
        )}

        {outcome.status === "taken-elsewhere" && (
          <>
            <p data-testid="idp-link-callback-taken">
              Denne {displayName}-kontoen er allerede koblet til en annen bruker.
            </p>
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="idp-link-callback-continue"
              onClick={returnToOrigin}
            >
              Tilbake
            </button>
          </>
        )}

        {outcome.status === "cancelled" && (
          <>
            <p data-testid="idp-link-callback-cancelled">
              {outcome.message ?? "Avbrutt – prøv igjen."}
            </p>
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="idp-link-callback-continue"
              onClick={returnToOrigin}
            >
              Tilbake
            </button>
          </>
        )}

        {outcome.status === "relogin" && (
          <>
            <p data-testid="idp-link-callback-relogin">
              Logg inn på nytt for å fullføre koblingen.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="idp-link-callback-relogin-btn"
              onClick={onSessionExpired}
            >
              Logg inn igjen
            </button>
          </>
        )}

        {outcome.status === "error" && (
          <>
            <p data-testid="idp-link-callback-error">{outcome.message}</p>
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="idp-link-callback-continue"
              onClick={returnToOrigin}
            >
              Tilbake
            </button>
          </>
        )}
      </section>
    </div>
  );
}
